import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../lib/auth';

interface LoginPageProps {
  onLogin: () => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(email, password);
      if (result.success) {
        const user = authService.getCurrentUser();
        if (user?.role === 'admin') {
          onLogin();
          navigate('/');
        } else {
          setError(t('login.accessDenied'));
          authService.logout();
        }
      } else {
        const errorMessage = typeof result.message === 'string'
          ? result.message
          : (result.message as { message?: string })?.message || (result as { error?: string }).error || t('login.loginFailed');
        setError(errorMessage);
      }
    } catch (err: unknown) {
      const errObj = err as { message?: string; error?: string };
      const errorMessage = errObj?.message || errObj?.error || t('login.error');
      setError(typeof errorMessage === 'string' ? errorMessage : t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="bg-white p-8 rounded-lg shadow-2xl w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-4xl">
              🍳
            </div>
          </div>
          <h1 className="text-3xl font-bold text-black mb-2">Cravela</h1>
          <p className="text-gray-600">{t('login.adminDashboard')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('login.tagline')}</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('login.email')}
            </label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-orange"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('login.password')}
            </label>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-primary-orange"
            />
          </div>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-primary-orange hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-orange focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {loading ? t('login.loggingIn') : t('login.signIn')}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-600">
          {t('login.defaultCredentials')}
        </p>

        <div className="mt-4 flex justify-center gap-2">
          <button type="button" onClick={() => i18n.changeLanguage('en')} className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100">
            EN
          </button>
          <button type="button" onClick={() => i18n.changeLanguage('ar')} className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100">
            العربية
          </button>
        </div>
      </div>
    </div>
  );
}
